import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import { useUserStore } from './user'

import { notificationService } from '@/services/notification'

export const useConversationsStore = defineStore('conversations', () => {
    const conversations = ref([])
    const activeConversationId = ref(null)
    const loading = ref(false)
    const pollingInterval = ref(10000) // Aumentado para 10s para maior estabilidade
    const error = ref(null)

    const userStore = useUserStore()

    const activeConversation = computed(() =>
        conversations.value.find(c => c.id === activeConversationId.value)
    )

    const unreadCount = computed(() => {
        return conversations.value.reduce((sum, c) => {
            // Converter para número para remover zeros à esquerda
            const count = parseInt(c.unread_count) || 0
            return sum + count
        }, 0)
    })

    async function fetchConversations(filters = {}) {
        loading.value = true
        error.value = null
        try {
            const queryParams = { limit: 1000, ...filters }
            const { data } = await api.get('/api/conversations', { params: queryParams })

            // Backend retorna { success: true, data: [...] }
            // Precisamos extrair o array de dentro de data.data
            if (data && data.data && Array.isArray(data.data)) {
                // Normalizar dados ao carregar
                conversations.value = data.data.map(conv => ({
                    ...conv,
                    unread_count: parseInt(conv.unread_count) || 0,
                    lead_score: parseInt(conv.lead_score) || 0,
                    lead_replied: parseInt(conv.lead_replied) || 0
                }))
            } else if (Array.isArray(data)) {
                // Fallback se retornar array direto
                conversations.value = data.map(conv => ({
                    ...conv,
                    unread_count: parseInt(conv.unread_count) || 0,
                    lead_score: parseInt(conv.lead_score) || 0,
                    lead_replied: parseInt(conv.lead_replied) || 0
                }))
            } else {
                // Se não for array, inicializar vazio
                console.warn('API retornou formato inesperado:', data)
                conversations.value = []
            }

            return data
        } catch (err) {
            error.value = err.message
            console.error('Error fetching conversations:', err)
            conversations.value = [] // Garantir que é array mesmo em erro
            throw err
        } finally {
            loading.value = false
        }
    }

    async function fetchUnread() {
        try {
            const params = {}
            if (userStore.currentUser?.id) {
                params.assigned_to = userStore.currentUser.id
            }

            const { data } = await api.get('/api/conversations/unread', { params })

            // Garantir que conversations.value é array
            if (!Array.isArray(conversations.value)) {
                conversations.value = []
            }

            // Extrair array de conversas da resposta
            let unreadConversations = []
            if (data && data.conversations && Array.isArray(data.conversations)) {
                unreadConversations = data.conversations
            } else if (data && data.data && Array.isArray(data.data)) {
                unreadConversations = data.data
            } else if (Array.isArray(data)) {
                unreadConversations = data
            }

            if (unreadConversations.length > 0) {
                let newConversations = 0
                let newMessages = 0

                // Update existing conversations with new messages
                unreadConversations.forEach(updated => {
                    // Normalizar dados antes de processar
                    const normalized = {
                        ...updated,
                        unread_count: parseInt(updated.unread_count) || 0,
                        lead_score: parseInt(updated.lead_score) || 0
                    }

                    const index = conversations.value.findIndex(c => c.id === normalized.id)
                    if (index >= 0) {
                        const prevUnread = parseInt(conversations.value[index].unread_count) || 0
                        if (normalized.unread_count > prevUnread) {
                            newMessages++
                        }
                        conversations.value[index] = { ...conversations.value[index], ...normalized }
                    } else {
                        conversations.value.unshift(normalized)
                        newConversations++
                    }
                })

                // Sort by last_message_at
                conversations.value.sort((a, b) =>
                    new Date(b.last_message_at) - new Date(a.last_message_at)
                )

                // Disparar notificação agregada (uma por ciclo, não uma por mensagem)
                if (newConversations > 0) {
                    notificationService.notify(
                        'Nova conversa',
                        `${newConversations} novo(s) lead(s) aguardando atendimento`
                    )
                } else if (newMessages > 0) {
                    notificationService.notify(
                        'Nova mensagem',
                        `${newMessages} mensagem(ns) não lida(s)`
                    )
                }
            }
        } catch (err) {
            console.error('Error fetching unread conversations:', err)
            // Não propagar erro para não quebrar polling
        }
    }

    function startPolling(interval = 5000) { // 5 segundos
        if (pollingInterval.value) return

        // Initial fetch
        fetchUnread()

        // Start polling
        pollingInterval.value = setInterval(() => {
            fetchUnread()
        }, interval)
    }

    function stopPolling() {
        if (pollingInterval.value) {
            clearInterval(pollingInterval.value)
            pollingInterval.value = null
        }
    }

    async function setActiveConversation(id) {
        activeConversationId.value = id

        // Mark as read locally and on backend
        const conversation = conversations.value.find(c => c.id === id)
        if (conversation && conversation.unread_count > 0) {
            conversation.unread_count = 0

            try {
                // Silently update backend
                await api.put(`/api/conversations/${id}/read`)
            } catch (err) {
                console.error('Error marking conversation as read:', err)
                // Don't throw/alert as this is a background sync
            }
        }
    }

    async function assignConversation(conversationId, userId) {
        try {
            const { data } = await api.put(`/api/conversations/${conversationId}/assign`, {
                assigned_to: userId
            })

            // Update local state
            const index = conversations.value.findIndex(c => c.id === conversationId)
            if (index >= 0) {
                conversations.value[index].assigned_to = userId
            }

            return data
        } catch (err) {
            console.error('Error assigning conversation:', err)
            throw err
        }
    }

    async function updateStatus(conversationId, status) {
        try {
            const { data } = await api.put(`/api/conversations/${conversationId}/status`, {
                lead_status: status
            })

            // Update local state
            const index = conversations.value.findIndex(c => c.id === conversationId)
            if (index >= 0) {
                conversations.value[index].lead_status = status
            }

            return data
        } catch (err) {
            console.error('Error updating conversation status:', err)
            throw err
        }
    }

    return {
        conversations,
        activeConversationId,
        activeConversation,
        unreadCount,
        loading,
        error,
        fetchConversations,
        fetchUnread,
        startPolling,
        stopPolling,
        setActiveConversation,
        assignConversation,
        updateStatus
    }
})
